<script lang="ts" setup>
export interface DocumentsTableSimpleProps {
    data: DocumentGroup[]
    initialCount?: number
    collapsable?: boolean
    caption?: string
}

const props = withDefaults(defineProps<DocumentsTableSimpleProps>(), {
    initialCount: 5,
    collapsable: false,
    caption: ''
})

const showAll = ref<boolean>(props.initialCount >= props.data.length)
const tableRef = ref<HTMLTableElement>()

const formattedData = computed<DocumentGroup[]>((): DocumentGroup[] => {
    if (!props.collapsable) return props.data

    return showAll.value
        ? props.data
        : props.data.slice(0, props.initialCount)
})

// WCAG 2.4.3: Focus management - move focus to first new item after expanding
const handleShowAll = () => {
    const firstNewIndex = props.initialCount
    showAll.value = true

    nextTick(() => {
        if (tableRef.value) {
            const rows = tableRef.value.querySelectorAll<HTMLTableRowElement>('tbody tr[data-document-row]')
            const firstNewRow = rows[firstNewIndex]
            if (firstNewRow) {
                firstNewRow.focus()
            }
        }
    })
}

const pdfSource = ref<string | undefined>(undefined)
</script>

<template>
  <div class="documents-table-simple-scroll overflow-auto">
    <table
      ref="tableRef"
      class="documents-table-simple w-100"
    >
      <caption
        v-if="props.caption"
        class="visually-hidden"
      >
        {{ props.caption }}
      </caption>
      <thead>
        <tr>
          <th
            scope="col"
            tabindex="0"
            class="text-start bg-gray-3 ps-9 pe-4 py-2 rounded-start-3 fs-small"
          >
            {{ $t('Názov dokumentu') }}
          </th>
          <th
            scope="col"
            class="d-none d-md-table-cell text-end bg-gray-3 ps-4 pe-9 py-2 rounded-end-3 fs-small"
          >
            <span class="visually-hidden">{{ $t('Akcie') }}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <template
          v-for="(group, groupIndex) in formattedData"
          :key="`document-group-${groupIndex}`"
        >
          <DocumentsTableSimpleRow
            v-for="(document, docIndex) in group.documents"
            :key="`document-${docIndex}`"
            :document="document"
            @open-pdf="src => (pdfSource = src)"
          />
        </template>
      </tbody>
    </table>

    <VButton
      v-if="props.collapsable && !showAll"
      color="link"
      type="button"
      class="d-flex align-items-center text-secondary mt-4"
      @click="handleShowAll"
    >
      {{ $t('Zobraziť ďalšie dokumenty') }}
      <svg
        class="d-block ms-1 text-secondary"
        aria-hidden="true"
        width="8"
        height="8"
      >
        <use
          :xlink:href="`#${
            props.collapsable
              ? 'angle-down'
              : 'angle-right'
          }`"
        />
      </svg>
    </VButton>
  </div>

  <ClientOnly>
    <ModalPdfPreview
      v-if="pdfSource"
      :source="pdfSource"
      @close="pdfSource = undefined"
    />
  </ClientOnly>

  <IconsWrapper>
    <IconsSymbolDownload />
    <IconsSymbolFile />
  </IconsWrapper>
</template>

<style lang="scss" scoped>
@import './DocumentsTableSimple.scss';
</style>
